import _ from 'lodash';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import {
  Button,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  Stack,
  TextContainer,
  TextStyle
} from '@shopify/polaris';
import { getFormValues, reduxForm, reset } from 'redux-form';

import TitleHelp from 'src/components/views/common/title-help';
import DidYouKnow from 'src/components/views/advanced-search/semantic-search/did-you-know';
import SemanticSettings from 'src/components/views/advanced-search/semantic-search/semantic-settings';
import SemanticSimulator from 'src/components/views/advanced-search/semantic-search/semantic-simulator';
import SemanticProcessingBanner from 'src/components/views/advanced-search/semantic-search/semantic-processing-banner';

import * as SemanticActions from 'actions/semantic-action';
import DisableSemanticModal from 'src/components/views/advanced-search/semantic-search/disable-semantic-modal';
import history from 'src/routes/history';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import { allowUsingNewSearchSettings } from 'src/helpers/search-settings-helper';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent_SemanticSearch } from 'src/integrations/fullStory';
import { routes } from 'src/routes';
import { URL } from 'src/constants/links';

const SemanticSearch = props => {
  const {
    i18n,
    semanticSetting = {},
    formValues = {},
    pristine,
    updateSubmitting,
    resetForm,
    dirty,
    changeSettings,
    getSettings,
    enableAISearch,
    semanticLoading,
    isNewSearch
  } = props;
  const { available } = semanticSetting;
  const { enable } = formValues;
  const [openDisable, setOpenDisable] = React.useState(false);

  useEffect(() => {
    if (isNewSearch) {
      getSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewSearch]);

  const handleSave = data => {
    const meta = {
      onSuccess: () => {
        // Tracking even enable/disabled semantic search
        mixpanelV3Helper.trackSemanticSearch(semanticSetting, data);

        // Track Full Story event
        trackFSEvent_SemanticSearch(semanticSetting, data);
      }
    };
    changeSettings(
      {
        enable: data.enable,
        weight: data.weight
      },
      meta
    );
  };

  const checkOpenConfirmDisabled = () => {
    if (!formValues.enable && semanticSetting.enable) {
      setOpenDisable(true);
    } else {
      handleSave(formValues);
    }
  };

  const getMainLayout = () => {
    if (_.isEmpty(semanticSetting) && semanticLoading) {
      return (
        <TextContainer>
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText />
        </TextContainer>
      );
    }

    return (
      <>
        <div>
          {i18n.translate('AdminHTML.SemanticSearch.SemanticSearch.description', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SemanticSearch.SemanticSearch.description_t1')}
              </TextStyle>
            ),
            t2: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SemanticSearch.SemanticSearch.description_t2')}
              </TextStyle>
            ),
            t3: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SemanticSearch.SemanticSearch.description_t3')}
              </TextStyle>
            )
          })}
        </div>
        <SemanticSettings />
        {enable && available && isNewSearch && (
          <>
            <div className="divider"></div>
            <SemanticSimulator />
          </>
        )}
      </>
    );
  };

  return (
    <div className={`BC-Page BC-Page__Semantic`}>
      <Page
        title={i18n.translate('AdminHTML.SemanticSearch.SemanticSearch.title')}
        primaryAction={
          <div className="BC-Page BC-Page__Button">
            <Stack>
              {!pristine && (
                <Button disabled={updateSubmitting} onClick={resetForm}>
                  {i18n.translate('AdminHTML.Common.Action.discard')}
                </Button>
              )}
              <Button
                primary
                onClick={checkOpenConfirmDisabled}
                disabled={pristine}
                loading={updateSubmitting}>
                {i18n.translate('AdminHTML.Common.Action.saveSettings')}
              </Button>
            </Stack>
          </div>
        }>
        <TitleHelp
          i18n={i18n}
          onAnalytics={() => history.push(routes.analytics.overviewSearch())}
          hrefUserManual={URL.Link_Search_Semantic_ManualGuide}
        />
        <Layout>
          <Layout.Section>
            <div className="BC-Container__Notifications">
              {/* TODO: Hide banner - Show when release semantic search */}
              <DidYouKnow />
              {enableAISearch && !_.isEmpty(semanticSetting) && !available && (
                <SemanticProcessingBanner />
              )}
            </div>
          </Layout.Section>

          <Layout.Section>{getMainLayout()}</Layout.Section>
        </Layout>
      </Page>
      <DisableSemanticModal
        onConfirm={() => {
          setOpenDisable(false);
          handleSave(formValues);
        }}
        onClose={() => setOpenDisable(false)}
        open={openDisable}
      />
      <RouteLeavingGuard
        when={dirty}
        // Navigate function
        navigate={path => history.push(path)}
        shouldBlockNavigation={dirty}
      />
    </div>
  );
};

const SemanticSearchForm = reduxForm({
  form: 'SemanticSearchForm',
  enableReinitialize: true
})(SemanticSearch);

const mapDispatchToProps = (dispatch, ownProps) => ({
  resetForm: () => dispatch(reset('SemanticSearchForm')),
  changeSettings: (data, meta) =>
    dispatch(SemanticActions.changeSemanticSettings(data, meta, ownProps.i18n)),
  getSettings: () => dispatch(SemanticActions.getSemanticSettings())
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('SemanticSearchForm')(state);
  const semanticSetting = _.get(state.semantic, 'settings', {});
  const searchVersion = _.get(state.oauth, 'tenantApp.parseParams.searchVersion', '1.0');
  const isNewSearch = allowUsingNewSearchSettings(searchVersion);

  return {
    formValues,
    initialValues: semanticSetting,
    semanticSetting,
    updateSubmitting: state.semantic.updateSubmitting,
    semanticLoading: state.semantic.settingGetting,
    checkSyncSemantic: state.semantic.checkSyncSemantic,
    // enableAISearch: _.get(state.oauth.tenantApp, 'settings.semanticSearch', false),
    enableAISearch: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.AI-synonyms',
      false
    ),
    isNewSearch
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SemanticSearchForm));
